import { Box, styled } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import {
    ScaleDisplayProps,
    useScaleDisplayHandler,
} from '~/hooks/viewer/useScaleDisplayHandler';
import { formatCm } from '~/utils/units';

const ScaleDisplayWrap = styled(Box)`
    position: absolute;
    z-index: 10;
    font-family: 'Roboto Mono';
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
`;

const ScaleBackbone = styled(Box)`
    height: 1px;
    width: 100%;
    background: ${({ theme }) => theme.palette.primary.main};
`;

const ScaleRulerTick = styled(Box)<{
    positionPx: number;
    heightPx: number;
}>`
    position: absolute;
    left: ${({ positionPx }) => `${positionPx}px`};
    bottom: 0;
    width: 2px;
    height: ${({ heightPx }) => `${heightPx}px`};
    background: ${({ theme }) => theme.palette.primary.main};
`;

const TickLabel = styled(Box)<{ positionPx: number }>`
    position: absolute;
    left: ${({ positionPx }) => `${positionPx}px`};
    bottom: 0;
    transform: translateX(-50%);
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: rgba(0, 0, 0, 0.4);
    padding: 1px 4px;
    border-radius: 2px;
    font-size: 10px;
    font-weight: 400;
    white-space: nowrap;
    user-select: none;
`;

const UnitLabel = styled(Box)`
    position: absolute;
    left: calc(100% + 4px);
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: rgba(0, 0, 0, 0.4);
    padding: 1px 4px;
    margin-left: 4px;
    border-radius: 2px;
    font-size: 10px;
    font-weight: 400;
    white-space: nowrap;
    user-select: none;
`;

// frequency of major ticks (every 5th tick)
const MAJOR_TICK_FREQUENCY = 5;

const MAJOR_TICK_LENGTH_PX = 10;
const REGULAR_TICK_LENGTH_PX = 6;
const MINOR_TICK_LENGTH_PX = 3;

// number of subdivisions (sub-ticks) between major ticks on the scale bar
const INNER_SUB_TICK_COUNT = 3;

/** Minimum pixel gap between major ticks before intermediate labels are hidden */
const MIN_LABEL_SPACING_PX = 28;

// Conversion factor from millimeters to centimeters for display purposes
const MM_PER_CM = 10;

const ScaleDisplay = ({ view, canvasRef }: ScaleDisplayProps) => {
    const { barPixelWidth, subdivisionCount, totalDistanceMm } =
        useScaleDisplayHandler({ view, canvasRef });

    const totalDistanceCm = totalDistanceMm / MM_PER_CM;

    const { tickElements, labelElements } = useMemo(() => {
        const ticks: ReactNode[] = [];
        const labels: ReactNode[] = [];

        if (barPixelWidth <= 0 || subdivisionCount <= 0) {
            return { tickElements: null, labelElements: null };
        }

        const majorStep = Math.max(
            1,
            Math.round(subdivisionCount / MAJOR_TICK_FREQUENCY)
        );
        const stepPx = barPixelWidth / subdivisionCount;
        const cmPerSubdivision = totalDistanceCm / subdivisionCount;

        // Collect major tick indices
        const majorIndices: number[] = [];
        for (let i = 0; i <= subdivisionCount; i++) {
            if (i === 0 || i === subdivisionCount || i % majorStep === 0) {
                majorIndices.push(i);
            }
        }

        // Determine whether intermediate labels have enough room
        const majorStepPx = majorStep * stepPx;
        const showIntermediateLabels = majorStepPx >= MIN_LABEL_SPACING_PX;

        // Build ticks and labels
        for (let i = 0; i <= subdivisionCount; i++) {
            const positionPx = (i / subdivisionCount) * barPixelWidth;
            const isMajor = majorIndices.includes(i);
            const tickHeight = isMajor
                ? MAJOR_TICK_LENGTH_PX
                : REGULAR_TICK_LENGTH_PX;

            ticks.push(
                <ScaleRulerTick
                    key={`tick-${i}`}
                    positionPx={Math.round(positionPx)}
                    heightPx={tickHeight}
                />
            );

            if (isMajor) {
                const isEndpoint = i === 0 || i === subdivisionCount;
                // Always show start/end labels; show intermediate only when space allows
                if (isEndpoint || showIntermediateLabels) {
                    const valueCm = i * cmPerSubdivision;
                    labels.push(
                        <TickLabel
                            key={`label-${i}`}
                            positionPx={Math.round(positionPx)}
                        >
                            {formatCm(valueCm, 1)}
                        </TickLabel>
                    );
                }
            }
        }

        // Sub-ticks between major ticks
        for (let m = 0; m < majorIndices.length - 1; m++) {
            const a = majorIndices[m];
            const b = majorIndices[m + 1];
            const gap = b - a;
            for (let k = 1; k <= INNER_SUB_TICK_COUNT; k++) {
                const fraction = k / (INNER_SUB_TICK_COUNT + 1);
                const idxFloat = a + fraction * gap;
                const subPosition = idxFloat * stepPx;
                ticks.push(
                    <ScaleRulerTick
                        key={`sub-tick-${m}-${k}`}
                        positionPx={Math.round(subPosition)}
                        heightPx={MINOR_TICK_LENGTH_PX}
                    />
                );
            }
        }

        return { tickElements: ticks, labelElements: labels };
    }, [barPixelWidth, subdivisionCount, totalDistanceCm]);

    const width = Math.max(4, Math.round(barPixelWidth));
    const labelRowHeight = 14;

    // Don't render anything until measurements are ready to avoid transient flicker
    if (barPixelWidth <= 0 || totalDistanceMm <= 0) {
        return null;
    }

    return (
        <ScaleDisplayWrap>
            {/* Ticks, backbone, and unit label */}
            <Box
                sx={{
                    position: 'relative',
                    width,
                    height: MAJOR_TICK_LENGTH_PX,
                }}
            >
                {tickElements}
                <ScaleBackbone sx={{ position: 'absolute', bottom: 0 }} />
                <UnitLabel>cm</UnitLabel>
            </Box>

            {/* Numeric distance labels (cm) below the ruler */}
            <Box
                sx={{
                    position: 'relative',
                    width,
                    height: labelRowHeight,
                    marginTop: 1,
                }}
            >
                {labelElements}
            </Box>
        </ScaleDisplayWrap>
    );
};

export default ScaleDisplay;
